import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { NOTIF_TYPES } from '@private/notifications';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import api from 'api';
import { PORTAL_USERS_KEYS } from 'api/portalUser/constants';
import {
  IActivateFilterPayload,
  IActivePreset,
  IAmountDisplayPayload,
  IFilterPayload,
  IOrganizationSettings,
  IOrganizationThemePayload,
  IPortalUserConfig,
  ISavedPreset,
  IUserConfigPayload,
} from 'api/portalUser/types';
import { useAppDispatch } from 'hooks/storeHooks';
import { SESSION_KEYS } from 'queries/session/constants';
import { getTransactionReportingPeriod } from 'queries/session/utils';
import {
  setLanguage,
  setOrgnSettings,
  setTheme,
  setTransactionReportingPeriod,
  utcTimeZone,
} from 'store/userConfiguration';
import { WORKBOOK_PAGE_KEY } from 'utils/amountDisplay';
import { QueryError } from 'utils/types';

import { notificationService } from '../../utils/notificationService';
import { normalizeQueryKey } from '../utils';

export const useGetPortalUserConfig = (): UseQueryResult<IPortalUserConfig> => {
  return useQuery({
    queryFn: api.portalUser.getUserConfig,
    queryKey: normalizeQueryKey([PORTAL_USERS_KEYS.PORTAL_USER]),
  });
};

export const useGetOrganizationSettings = (): UseQueryResult<IOrganizationSettings> => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  const query = useQuery({
    gcTime: Infinity,
    queryFn: api.portalUser.getOrganizationSettings,
    queryKey: normalizeQueryKey([PORTAL_USERS_KEYS.ORGANIZATION_SETTINGS]),
    retry: 1,
  });

  useEffect(() => {
    if (query.isSuccess) {
      dispatch(setOrgnSettings(query.data));
    }
  }, [dispatch, query.data, query.isSuccess]);

  useEffect(() => {
    if (query.isFetched) {
      queryClient.setQueryData([SESSION_KEYS.IS_THEME_REQUESTED], true);
    }
  }, [query.isFetched, queryClient]);

  return query;
};

export const useSaveOrganizationTheme = (): UseMutationResult<
  IOrganizationSettings,
  QueryError,
  IOrganizationThemePayload,
  string
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation({
    mutationFn: api.portalUser.saveOrganizationTheme,
    onError: (error, _variables, context) => {
      notificationService.show({
        dismissIn: 10000,
        message: error?.response?.data?.message || t('settings.notifications.save.error'),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: context,
      });
    },
    onMutate: () => {
      return notificationService.show({
        message: t('settings.notifications.save.loading'),
        title: t('notifications.loadingTitle'),
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: (orgnSettings, { canApply }, context) => {
      if (canApply) {
        queryClient.setQueryData([PORTAL_USERS_KEYS.ORGANIZATION_SETTINGS], orgnSettings);
        dispatch(setOrgnSettings(orgnSettings));
      }

      notificationService.show({
        message: t('settings.notifications.save.success'),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: context,
      });
    },
  });
};

export const useSaveUserConfigParam = (): UseMutationResult<
  IPortalUserConfig,
  QueryError,
  IUserConfigPayload,
  string
> => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.portalUser.saveUserConfigParam,
    onError: (error) => {
      notificationService.show({
        message: error?.response?.data?.message || t('portalUser.saveTheme.error'),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
      });
    },
    onSettled: (_resp, _error, { customCalendarPeriodDays, language, themeVariant }) => {
      if (themeVariant) {
        dispatch(setTheme(themeVariant));
      }
      if (language) {
        dispatch(setLanguage(language));
      }
      if (customCalendarPeriodDays !== undefined) {
        if (customCalendarPeriodDays) {
          const { endDate, startDate } = getTransactionReportingPeriod(
            utcTimeZone,
            customCalendarPeriodDays,
          );
          dispatch(setTransactionReportingPeriod({ endDate, startDate }));
        } else {
          const { endDate, startDate } = getTransactionReportingPeriod(utcTimeZone);
          dispatch(setTransactionReportingPeriod({ endDate, startDate }));
        }
      }
    },
    onSuccess: (
      _,
      {
        customCalendarPeriodDays,
        expandTable,
        groupEditConfirmation,
        hideMaxClientsStats,
        language,
        pageKey,
        recordsPerPage,
        rulesetDefaultCopyInclude,
        themeVariant: theme,
      },
    ) => {
      const portalUser = queryClient.getQueryData<IPortalUserConfig>([
        PORTAL_USERS_KEYS.PORTAL_USER,
      ]);

      if (portalUser) {
        // Copy instead of mutating the cached object in place: an in-place edit
        // leaves react-query's structural sharing seeing prev === next, so it
        // keeps the old reference and consumers (e.g. useGridExpand) never
        // re-render. theme/language survive that via the onSettled redux dispatch,
        // but expandTable has no such fallback and relies on the query updating.
        const settings = { ...portalUser.settings };
        if (recordsPerPage) {
          settings.recordsPerPage = recordsPerPage;
        }
        if (theme) {
          settings.theme = theme;
        }
        if (language) {
          settings.language = language;
        }
        if (groupEditConfirmation !== undefined) {
          settings.groupEditConfirmation = groupEditConfirmation;
        }
        if (rulesetDefaultCopyInclude !== undefined) {
          settings.rulesetDefaultCopyInclude = rulesetDefaultCopyInclude;
        }
        if (hideMaxClientsStats !== undefined) {
          settings.hideMaxClientsStats = hideMaxClientsStats;
        }
        if (customCalendarPeriodDays !== undefined) {
          settings.customCalendarPeriodDays = customCalendarPeriodDays ?? undefined;
        }
        if (expandTable !== undefined && pageKey) {
          const pageConfig = settings.pages[pageKey] || {
            activePreset: null,
            presets: [],
          };
          settings.pages = {
            ...settings.pages,
            [pageKey]: { ...pageConfig, expandTable },
          };
        }
        queryClient.setQueryData([PORTAL_USERS_KEYS.PORTAL_USER], {
          ...portalUser,
          settings,
        });
      }
    },
    throwOnError: false,
  });
};

export const useSaveAmountDisplayConfig = (): UseMutationResult<
  void,
  QueryError,
  IAmountDisplayPayload,
  string
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.portalUser.saveAmountDisplayConfig,
    onError: (error) => {
      notificationService.show({
        message:
          error?.response?.data?.message ||
          t('settlementContract.workbook.amountDisplay.notifications.save.error'),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
      });
    },
    onSuccess: (_, variables) => {
      const { applyToAll, decimalSeparator, thousandsSeparator } = variables;
      const pageKey = applyToAll ? WORKBOOK_PAGE_KEY : variables.pageKey;
      const portalUser = queryClient.getQueryData<IPortalUserConfig>([
        PORTAL_USERS_KEYS.PORTAL_USER,
      ]);

      if (portalUser) {
        // Copy instead of mutating in place — see the structural-sharing note
        // in useSaveUserConfigParam above
        const settings = { ...portalUser.settings };
        const pages = { ...settings.pages };

        pages[pageKey] = {
          ...(pages[pageKey] || { activePreset: null, presets: [] }),
          amountDisplay: { decimalSeparator, thousandsSeparator },
        };

        if (applyToAll) {
          // the user-global save also wipes every per-contract record under the base key
          Object.keys(pages).forEach((key) => {
            if (key.startsWith(`${pageKey}/`)) {
              const { amountDisplay: _removed, ...pageConfig } = pages[key];
              pages[key] = pageConfig;
            }
          });
        }

        settings.pages = pages;

        queryClient.setQueryData([PORTAL_USERS_KEYS.PORTAL_USER], {
          ...portalUser,
          settings,
        });
      }

      notificationService.show({
        message: t(
          'settlementContract.workbook.amountDisplay.notifications.save.success',
        ),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
      });
    },
    throwOnError: false,
  });
};

export const useResetAmountDisplayConfig = (): UseMutationResult<
  void,
  QueryError,
  string,
  string
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.portalUser.resetAmountDisplayConfig,
    onError: (error) => {
      notificationService.show({
        message:
          error?.response?.data?.message ||
          t('settlementContract.workbook.amountDisplay.notifications.reset.error'),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
      });
    },
    onSuccess: (_, pageKey) => {
      const portalUser = queryClient.getQueryData<IPortalUserConfig>([
        PORTAL_USERS_KEYS.PORTAL_USER,
      ]);

      if (portalUser?.settings.pages[pageKey]) {
        // Copy instead of mutating in place — see the structural-sharing note
        // in useSaveUserConfigParam above
        const settings = { ...portalUser.settings };
        const { amountDisplay: _removed, ...pageConfig } = settings.pages[pageKey];
        settings.pages = { ...settings.pages, [pageKey]: pageConfig };

        queryClient.setQueryData([PORTAL_USERS_KEYS.PORTAL_USER], {
          ...portalUser,
          settings,
        });
      }

      notificationService.show({
        message: t(
          'settlementContract.workbook.amountDisplay.notifications.reset.success',
        ),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
      });
    },
    throwOnError: false,
  });
};

export const useSaveFiltersPreset = (): UseMutationResult<
  { id: string },
  QueryError,
  IFilterPayload,
  string
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.portalUser.saveFiltersPreset,
    onError: (error) => {
      notificationService.show({
        message:
          error?.response?.data?.message || t('portalUser.filtersPresets.save.error'),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
      });
    },
    onSuccess: (newPreset, { isRecent, pageKey, presetId }) => {
      const portalUser = queryClient.getQueryData<IPortalUserConfig>([
        PORTAL_USERS_KEYS.PORTAL_USER,
      ]);

      if (portalUser) {
        const { settings } = portalUser;

        const prevPresets = settings.pages[pageKey]?.presets || [];
        let presetUpdated = false;

        const newPresetsList = prevPresets.reduce<ISavedPreset[]>((prev, preset) => {
          if ((preset.isRecent && isRecent) || preset.id === presetId) {
            prev.push(newPreset);
            presetUpdated = true;
          } else {
            prev.push(preset);
          }
          return prev;
        }, []);

        queryClient.setQueryData([PORTAL_USERS_KEYS.PORTAL_USER], {
          ...portalUser,
          settings: {
            ...settings,
            pages: {
              ...settings.pages,
              [pageKey]: {
                ...(settings.pages[pageKey] || {}),
                activePreset: newPreset.id,
                presets:
                  (isRecent || presetId) && prevPresets.length && presetUpdated
                    ? newPresetsList
                    : [...prevPresets, newPreset],
              },
            },
          },
        });
      }
    },
    throwOnError: false,
  });
};

export const useActivateFiltersPreset = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  IActivateFilterPayload,
  string
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.portalUser.activateFiltersPreset,
    onError: (error) => {
      notificationService.show({
        message:
          error?.response?.data?.message || t('portalUser.filtersPresets.activate.error'),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
      });
    },
    onSuccess: (_response, { pageKey, presetId }) => {
      const portalUser = queryClient.getQueryData<IPortalUserConfig>([
        PORTAL_USERS_KEYS.PORTAL_USER,
      ]);

      if (portalUser) {
        const { settings } = portalUser;
        queryClient.setQueryData([PORTAL_USERS_KEYS.PORTAL_USER], {
          ...portalUser,
          settings: {
            ...settings,
            pages: {
              ...settings.pages,
              [pageKey]: {
                ...(settings.pages[pageKey] || {}),
                activePreset: presetId,
              },
            },
          },
        });
      }
    },
    throwOnError: false,
  });
};

export const useDeleteFiltersPreset = (): UseMutationResult<
  IActivePreset,
  QueryError,
  IActivateFilterPayload,
  string
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.portalUser.deleteFiltersPreset,
    onError: (error) => {
      notificationService.show({
        message:
          error?.response?.data?.message || t('portalUser.filtersPresets.activate.error'),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
      });
    },
    onSuccess: ({ activePreset }, { pageKey, presetId }) => {
      const portalUser = queryClient.getQueryData<IPortalUserConfig>([
        PORTAL_USERS_KEYS.PORTAL_USER,
      ]);

      if (portalUser) {
        const { settings } = portalUser;
        queryClient.setQueryData([PORTAL_USERS_KEYS.PORTAL_USER], {
          ...portalUser,
          settings: {
            ...settings,
            pages: {
              ...settings.pages,
              [pageKey]: {
                ...(settings.pages[pageKey] || {}),
                activePreset,
                presets: settings.pages[pageKey].presets.filter(
                  (preset) => preset.id !== presetId,
                ),
              },
            },
          },
        });
      }
    },
    throwOnError: false,
  });
};
